import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, FileText, IndianRupee, Layers, Megaphone, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";

const Tile = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
      {/* Static class — Tailwind's JIT can't see interpolated class names. */}
      <Icon size={13} className="text-violet-500" />
      {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

const STATUS_TONE = {
  ACTIVE: "bg-emerald-50 text-emerald-700",
  TRIALING: "bg-blue-50 text-blue-700",
  PAST_DUE: "bg-amber-50 text-amber-700",
  CANCELLED: "bg-rose-50 text-rose-700",
  NONE: "bg-slate-100 text-slate-600",
};

export default function PlatformDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/platform/overview").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="text-sm text-slate-500">Loading platform overview…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Platform Overview</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every account, plan and announcement across {window.location.host}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile icon={Building2} label="Accounts" value={t.accounts ?? t.organizations} testid="tile-orgs" />
        <Tile icon={Users} label="Users" value={t.users} testid="tile-users" />
        <Tile icon={FileText} label="Vouchers" value={t.vouchers} testid="tile-vouchers" />
        <Tile icon={Building2} label="Tally companies" value={t.companies} testid="tile-companies" />
        <Tile icon={IndianRupee} label="MRR (active)" value={inr(t.mrr)} testid="tile-mrr" />
        <Tile icon={Layers} label="Active plans" value={t.plans} testid="tile-plans" />
        <Tile icon={Megaphone} label="Live announcements" value={t.announcements} testid="tile-announcements" />
        <Tile icon={Ban} label="Suspended" value={t.suspended} testid="tile-suspended" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white lg:col-span-2">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">
            Newest accounts
          </div>
          <table className="w-full text-sm" data-testid="recent-orgs-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Organisation</th>
                <th className="px-3 py-2 text-left font-semibold">GSTIN</th>
                <th className="px-3 py-2 text-left font-semibold">Plan</th>
                <th className="px-4 py-2 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.recent_organizations.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50 transition-colors duration-150">
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/platform/organizations/${o.id}`}
                      className="font-medium text-violet-700 hover:text-violet-900"
                    >
                      {o.name}
                    </Link>
                    {o.company_count > 1 && (
                      <span className="ml-1.5 text-[11px] text-slate-500">{o.company_count} companies</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">
                    {o.gstin || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-xs">{o.subscription?.plan_name || "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                        STATUS_TONE[o.subscription?.status] || STATUS_TONE.NONE
                      }`}
                    >
                      {o.subscription?.status || "NONE"}
                    </span>
                  </td>
                </tr>
              ))}
              {!data.recent_organizations.length && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                    No organisations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">
            Subscribers by plan
          </div>
          <div className="divide-y divide-slate-100" data-testid="by-plan-list">
            {data.by_plan.map((p) => (
              <div key={p.code} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className="font-mono text-xs font-semibold">{p.code}</span>
                <span className="tabular-nums text-slate-600">{p.count}</span>
              </div>
            ))}
            {!data.by_plan.length && (
              <div className="px-4 py-10 text-center text-sm text-slate-500">
                No organisation is on a plan yet
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
